import {AfterViewInit, Component, inject, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { Observable, of, Subject } from "rxjs";
import { CentreOption } from "../../../../../interfaces/Centres";
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import { Employee } from "../../../../../interfaces/Employee";
import { InductionService } from "../induction.service";
import { ActivatedRoute, Router } from "@angular/router";
import { CentreService } from "../../../../../services/centre.service";
import { EmployeeService } from "../../../../../services/employee.service";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import {EducationInduction} from "../induction.model";
import {LogService} from "../../../../../services/log.service";
import firebase from "firebase/compat/app";
import {map, switchMap, take, takeUntil} from "rxjs/operators";
import _default from "chart.js/dist/core/core.interaction";
import index = _default.modes.index;

@Component({
  selector: 'app-add-edit-induction',
  templateUrl: './add-edit-induction.component.html',
  styleUrls: ['./add-edit-induction.component.scss']
})
export class AddEditInductionComponent implements OnInit, OnDestroy {
  private _destroyed$ = new Subject<void>();
  allCenters$!: Observable<CentreOption[]>;
  displayCentre = (c: any) => c?.centreName;
  topic: string[] = [];
  content: string[] = [];
  dayWiseData: any = {
    1: {
      topic: 'HR - Induction & Introduction',
      content: [
        'Induction & Introduction to CM & CC of the centre',
        'Shared SOP',
        'Shared Attendance sheet',
        'Daily Task sheet'
      ]
    },
    2: {
      topic: 'Teaching Introduction & Pre-Baseline',
      content: [
        'Significance of Non-Formal Education Intervention',
        'Types of sessions and number of sessions',
        'Pre-Baseline Activity',
        'Rapport Building Sessions',
        'How to calculate ages of children',
        'Observation of teaching sessions'
      ]
    },
    3: {
      topic: 'Baseline Assessment',
      content: [
        'Recap of previous day',
        'Weekly teachers meeting',
        'Learning Levels and content',
        'Conducting Baseline tests',
        'Correction & marking',
        'Sharing question papers'
      ]
    },
    4: {
      topic: 'IEP & Sessions',
      content: [
        'Recap of previous day',
        'IEP – Individual Education Plan',
        'Educational sessions',
        'Special Day'
      ]
    },
    5: {
      topic: 'Assessment & Reports',
      content: [
        'Recap',
        'Intermediate Evaluation',
        'Discharge Assessment',
        'Report Card',
        'Weekly meeting'
      ]
    },
    6: {
      topic: 'Observation & Reports',
      content: [
        'Rapport session observation',
        'Social Impact Report',
        'Monthly & Quarterly reports',
        'Graduation process'
      ]
    },
    7: {
      topic: 'MySpace',
      content: [
        'Child list',
        'Group Session (All domains)',
        'Impact Report'
      ]
    },
    8: {
      topic: 'Child Safety & Library',
      content: [
        'Library corner sheet',
        'Good touch & bad touch'
      ]
    },
    9: {
      topic: 'Advanced MySpace',
      content: [
        'Child list',
        'Group Session',
        'Individual Session',
        'Follow up'
      ]
    },
    10: {
      topic: 'Final Review',
      content: [
        'Overview of induction',
        'Queries',
        'Session observation'
      ]
    }
  };
  // This is for the overall induction centre, not day-wise
  filteredCentres$!: Observable<CentreOption[]>;
  // This will hold filtered centres for each day
  dayFilteredCentres: Observable<CentreOption[]>[] = [];

  inductionForm!: FormGroup;
  observerName: any;
  allTeachers: any[] = [];
  employeeData!: Employee[];
  currentEditId: any;
  isLoading: boolean = false;
  selectedInductionCentre: any;
  isLinear = true;

  // Days management
  days: number[] = [1, 2, 3]; // Default 3 days
  maxDays = 10;

  private _formBuilder = inject(FormBuilder);

  constructor(
    private fb: FormBuilder,
    private educationInductionService: InductionService,
    private router: Router,
    private route: ActivatedRoute,
    private centreService: CentreService,
    private userService: EmployeeService,
    private mLogService: LogService,
    @Inject(MAT_DIALOG_DATA) public id: string | null,
    private dialogRef: MatDialogRef<AddEditInductionComponent>
  ) {
    this.allCenters$ = this.centreService.getAllCentres();
    this.filteredCentres$ = this.allCenters$; // For the overall induction centre
  }

  ngOnInit(): void {
    this.formInit();
    this.fetchAllTeachers();
    this.currentEditId = this.id;

    if (this.currentEditId) {
      this.loadEditData();
    }
  }

  // ensure selection state is clean when initializing the form
  formInit() {
    this.selectedInductionCentre = null;
    this.inductionForm = this.fb.group({
      inductionFor: ['', Validators.required],
      inductionCentreName: ['', [Validators.required, this.centreValidator.bind(this)]],
      inductionCentreCode: ['', [Validators.required, this.centreValidator.bind(this)]],
      days: this.fb.array([])
    });

    // Initialize with 3 days
    for (let i = 0; i < 3; i++) {
      this.getDaysFormArray().push(this.createDayFormGroup(i));
      this.dayFilteredCentres.push(this.allCenters$); // Initialize each day's filtered centres
    }
  }

  createDayFormGroup(index: number): FormGroup {
    const dayData = this.dayWiseData[index + 1] || { topic: '', content: [] };
    return this.fb.group({
      teacherName: ['', Validators.required],
      // initialize as null so UI components don't treat empty string as a selected value
      centreName: [null, [Validators.required, this.centreValidator.bind(this)]],
      centreCode: [null, [Validators.required, this.centreValidator.bind(this)]],
      date: ['', Validators.required],
      topic: [dayData.topic, Validators.required],
      content: [dayData.content.join('\n'), Validators.required], // convert array to text
      status: ['Pending'],
      suggestions: ['', Validators.maxLength(100)]
    });
  }

  getDaysFormArray(): FormArray {
    return this.inductionForm.get('days') as FormArray;
  }

  getDayFormGroup(index: number): FormGroup {
    return this.getDaysFormArray().at(index) as FormGroup;
  }

  addDay() {
    if (this.days.length < this.maxDays) {
      const index = this.days.length;
      this.days.push(index + 1);
      this.getDaysFormArray().push(this.createDayFormGroup(index));
      this.dayFilteredCentres.push(this.allCenters$); // Initialize new day's filtered centres
    }
  }

  removeDay(index: number, stepper: any) {
    // Don't allow removing if only 1 day left
    if (this.days.length <= 1) {
      alert('At least one day is required!');
      return;
    }

    // Confirm before removing
    const confirmRemove = confirm(`Are you sure you want to remove Day ${index + 1}?`);
    if (!confirmRemove) {
      return;
    }

    // Remove from arrays
    this.days.splice(index, 1);
    this.getDaysFormArray().removeAt(index);
    this.dayFilteredCentres.splice(index, 1); // Remove corresponding filtered centres observable

    // Navigate to previous step if available
    if (index > 0) {
      stepper.selectedIndex = index - 1;
    } else if (this.days.length > 0) {
      stepper.selectedIndex = 0;
    }
  }

  centreValidator(control: FormControl) {
    const value = control.value;
    if (!value) return { required: true };
    return null;
  }

  submitForm() {
    if (this.inductionForm.invalid) {
      alert('Please fill all required fields');
      return;
    }

    this.isLoading = true;

    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const username = `${userData.userName || ''}`.trim() || 'Unknown User';

    // Prepare payload with all days
    // Use getRawValue() so disabled controls (auto-selected centres) are included in the payload
    const daysData = (this.getDaysFormArray().getRawValue() as any[]).map((day: any) => ({
      ...day,
      date: new Date(day.date)
    }));

    const payload: any = {
      inductionFor: this.inductionForm.value.inductionFor,
      inductionCentreName: this.inductionForm.value.inductionCentreName,
      inductionCentreCode: this.inductionForm.value.inductionCentreCode,
      days: daysData
    };

    if (this.currentEditId) {
      // UPDATE
      payload.updatedBy = username;
      payload.updatedAt = firebase.firestore.FieldValue.serverTimestamp();

      this.educationInductionService.update(this.currentEditId, payload)
        .then(() => {
          this.mLogService.addLog({
            date: Date.now(),
            section: 'Education Induction',
            action: 'Updated',
            description: `${username} updated the Education Induction record`,
            currentIp: localStorage.getItem('currentip')!
          });
          this.afterSave('Updated successfully!');
        })
        .catch((error) => {
          console.error('Error updating Induction:', error);
          this.isLoading = false;
        });
    } else {
      // CREATE
      payload.createdBy = username;
      payload.createdAt = firebase.firestore.FieldValue.serverTimestamp();
      payload.status = 'Active';

      this.educationInductionService.create(payload)
        .then(() => {
          this.mLogService.addLog({
            date: Date.now(),
            section: 'Education Induction',
            action: 'Created',
            description: `${username} created a new Education Induction record`,
            currentIp: localStorage.getItem('currentip')!
          });
          this.afterSave('Saved successfully!');
        })
        .catch((error) => {
          console.error('Error creating Induction:', error);
          this.isLoading = false;
        });
    }
  }

  private loadEditData() {
    this.educationInductionService.getDataById(this.currentEditId).pipe(takeUntil(this._destroyed$)).subscribe((record: any) => {
      if (record) {
        this.inductionForm.patchValue({
          inductionFor: record.inductionFor,
          inductionCentreName: record.inductionCentreName,
          inductionCentreCode: record.inductionCentreCode,
        });
        this.selectedInductionCentre = { centreName: record.inductionCentreName, centreCode: record.inductionCentreCode };

        if (record.days && Array.isArray(record.days)) {
          this.getDaysFormArray().clear();
          this.days = [];
          this.dayFilteredCentres = []; // Clear and re-initialize

          record.days.forEach((day: any, index: number) => {
            this.days.push(index + 1);
            const dayGroup = this.createDayFormGroup(index);
            dayGroup.patchValue({
              ...day,
              date: day.date.toDate(),
            });
            this.getDaysFormArray().push(dayGroup);

            if (day.teacherName) {
              this.userService.getUserByUserName(day.teacherName).pipe(
                switchMap(res => {
                  if (res) {
                    const allowedCentreCodes = res.centresAllowed || [];
                    return this.allCenters$.pipe(
                      map(allCentres => allCentres.filter(c => allowedCentreCodes.includes(c.centreCode))),
                      map(filteredCentres => ({ res, filteredCentres }))
                    );
                  }
                  return of({ res: null, filteredCentres: [] });
                }),
                take(1)
              ).subscribe(({ res, filteredCentres }) => {
                if (res) {
                  this.dayFilteredCentres[index] = of(filteredCentres);

                  // If only one allowed centre, auto-select it and disable centre fields
                  if (filteredCentres.length === 1) {
                    const centreToSelect = filteredCentres[0];
                    dayGroup.patchValue({
                      centreName: centreToSelect.centreName,
                      centreCode: centreToSelect.centreCode
                    });
                    dayGroup.get('centreName')?.disable();
                    dayGroup.get('centreCode')?.disable();
                  } else {
                    // Multiple centres -> enable user selection
                    dayGroup.get('centreName')?.enable();
                    dayGroup.get('centreCode')?.enable();
                  }
                } else {
                  this.dayFilteredCentres[index] = this.allCenters$;
                  dayGroup.get('centreName')?.enable();
                  dayGroup.get('centreCode')?.enable();
                }
              });
            } else {
              this.dayFilteredCentres.push(this.allCenters$);
              dayGroup.get('centreName')?.enable();
            }
          });
        }
      }
    });
  }

  private afterSave(message: string) {
    this.isLoading = false;
    alert(message);
    this.dialogRef.close('saved');
  }

  fetchAllTeachers() {
    this.userService.getUsers().pipe(takeUntil(this._destroyed$)).subscribe(snapshotchanges => {
      this.employeeData = snapshotchanges.map(w => {
        let emp = w.payload.toJSON() as Employee;
        if (w.key != null) {
          emp['id'] = w.key;
        }
        return emp;
      });
      const namesToRemove = ['Shilpa Pahuja', 'Mukti Panchamia'];
      this.allTeachers = this.employeeData
        .filter(employee => (employee?.role === 'Teacher' || employee?.role === 'Sr Teacher') && employee.disabled !== true)
        .map(counsellingEmployee => ({ name: counsellingEmployee?.userName }))
        .filter(c => !namesToRemove.includes(c?.name))
        .sort((a, b) => a?.name.localeCompare(b?.name));
    });
  }

  resetForm() {
    this.inductionForm.reset();
    this.getDaysFormArray().clear();
    this.days = [1, 2, 3];
    this.dayFilteredCentres = []; // Clear and re-initialize
    for (let i = 0; i < 3; i++) {
      this.getDaysFormArray().push(this.createDayFormGroup(i));
      this.dayFilteredCentres.push(this.allCenters$);
    }
    this.currentEditId = null;
  }

  onCancel() {
    this.dialogRef.close();
  }

  onInductionForSelected(e: any) {
    if (e && e.name) {
      this.inductionForm.get('inductionFor')?.setValue(e.name);
    } else {
      this.inductionForm.get('inductionFor')?.setValue('');
    }
  }

  onTeacherSelected(selected: any, index: number) {
    const dayFormGroup = this.getDayFormGroup(index);
    dayFormGroup.get('teacherName')?.setValue(selected.name);

    this.userService.getUserByUserName(selected.name).pipe(
      switchMap(res => {
        if (res) {
          const allowedCentreCodes = res.centresAllowed || [];
          return this.allCenters$.pipe(
            map(allCentres => allCentres.filter(c => allowedCentreCodes.includes(c.centreCode))),
            map(filteredCentres => ({ res, filteredCentres }))
          );
        }
        return of({ res: null, filteredCentres: [] });
      }),
      take(1) // Ensure the subscription auto-completes
    ).subscribe(({ res, filteredCentres }) => {
      if (res) {
        this.dayFilteredCentres[index] = of(filteredCentres);

        if (filteredCentres.length === 1) {
          const centreToSelect = filteredCentres[0];
          dayFormGroup.patchValue({
            centreName: centreToSelect.centreName,
            centreCode: centreToSelect.centreCode
          });
          // Disable both name and code to prevent manual change
          dayFormGroup.get('centreName')?.disable();
          dayFormGroup.get('centreCode')?.disable();
        } else {
          // clear to null to keep consistency with initial state
          dayFormGroup.patchValue({
            centreName: null,
            centreCode: null
          });
          dayFormGroup.get('centreName')?.enable();
          dayFormGroup.get('centreCode')?.enable();
        }
      } else {
        this.dayFilteredCentres[index] = this.allCenters$;
        dayFormGroup.patchValue({
          centreName: null,
          centreCode: null
        });
        dayFormGroup.get('centreName')?.enable();
        dayFormGroup.get('centreCode')?.enable();
      }
    });
  }

  onCentreSelect($event: any, index: number) {
    const dayFormGroup = this.getDayFormGroup(index);
    if ($event && $event.centreName && $event.centreCode) {
      dayFormGroup.patchValue({
        centreName: $event.centreName,
        centreCode: $event.centreCode
      });
    } else {
      // clear to null for consistency with initial state
      dayFormGroup.patchValue({
        centreName: null,
        centreCode: null
      });
      dayFormGroup.get('centreName')?.markAsTouched();
      dayFormGroup.get('centreCode')?.markAsTouched();
    }
  }

  onInductionCentreSelect($event: any) {
    if ($event && $event.centreName && $event.centreCode) {
      this.inductionForm.patchValue({
        inductionCentreName: $event.centreName,
        inductionCentreCode: $event.centreCode
      });
      this.selectedInductionCentre = $event;
    } else {
      this.inductionForm.patchValue({
        inductionCentreName: '',
        inductionCentreCode: ''
      });
      this.selectedInductionCentre = null;
      this.inductionForm.get('inductionCentreName')?.markAsTouched();
      this.inductionForm.get('inductionCentreCode')?.markAsTouched();
    }
  }

  isFormValid(): boolean {
    const inductionForValid = this.inductionForm.get('inductionFor')?.valid;
    const inductionCentreNameValid = this.inductionForm.get('inductionCentreName')?.valid;
    const inductionCentreCodeValid = this.inductionForm.get('inductionCentreCode')?.valid;
    const allDaysValid = this.getDaysFormArray().controls.every(dayControl => dayControl.valid);
    return !!(inductionForValid && inductionCentreNameValid && inductionCentreCodeValid && allDaysValid);
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }
}
